/**
 *
 * Cascader组件的函数式重构版
 *
 * 状态管理: useCascaderState (useReducer统一管理)
 * 核心Hooks:
 * - useValue: value状态管理
 * - useOptions: options处理、搜索过滤
 * - useTiledContent: 平铺模式渲染
 *
 * 新增功能:
 * - loadData: 动态加载
 * - displayRender: 自定义显示
 * - dropdownRender: 自定义下拉
 * - expandTrigger/expandIcon: 展开控制
 * - 完整事件处理
 * - 完整生命周期管理
 */

import RcCascader from '@tinper/rc-cascader';
import classNames from 'classnames';
import { cloneDeep, isEqual } from 'lodash';
import KeyCode from 'rc-util/lib/KeyCode';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { setComponentSize } from '../../wui-core/src/componentStyle';
import { addEventListener, cssUtil, getNid, prefix, setComponentClass } from '../../wui-core/src/index';
import type { SizeType } from '../../wui-core/src/types/iCore';
import Empty from '../../wui-empty/src';
import Icon from '../../wui-icon/src';
import Input from '../../wui-input/src';
import { getLangInfo } from '../../wui-locale/src/tool';
import { ConfigContext } from '../../wui-provider/src/context';
import i18n from './i18n';

import type { CascaderOption, CascaderProps, FieldNames } from './iCascader';

// 导入已提取的Hooks
import { useCascaderState } from './hooks/useCascaderState';
import { useOptions } from './hooks/useOptions';
import { useTiledContent } from './hooks/useTiledContent';
import { useValue } from './hooks/useValue';

// 常量定义
const { SHOW_CHILD, SHOW_PARENT } = RcCascader;
const VALUE_SPLIT = '__RC_CASCADER_SPLIT__';
const keepFilteredValueField = '__KEEP_FILTERED_OPTION_VALUE';

// arrayTreeFilter工具函数
const arrayTreeFilter = (data: CascaderOption[], filterFn: any, options: any) => {
    options = options || {};
    options.childrenKeyName = options.childrenKeyName || "children";
    let children = data || [];
    let result = [];
    let level = 0;
    do {
        let foundItem = children.filter((item) => {
            return filterFn(item, level);
        })[0];
        if (!foundItem) {
            break;
        }
        result.push(foundItem);
        children = foundItem[options.childrenKeyName] || [];
        level += 1;
    } while (children.length > 0);
    return result;
};

/**
 * Cascader Ref接口
 */
export interface CascaderRef {
    focus: () => void;
    blur: () => void;
    // 平铺模式方法,供外部调用
    searchTiledItem: (val: { value: any[]; path: any; id?: any }) => void;
    onChangeValue: (val: {
        label: string;
        value: string;
        children: any;
        isLeaf?: boolean;
        isEnd?: any;
        NoSelected?: boolean;
    }) => void;
}

/**
 * Cascader - 函数式组件
 */
const Cascader: any = forwardRef((props: CascaderProps, ref: any) => {
    const {
        prefixCls = `${prefix}-cascader`,
        className,
        style,
        options: propsOptions = [],
        value: propsValue,
        defaultValue = [],
        onChange,
        onPopupVisibleChange,
        showSearch = false,
        fieldNames: propsFieldNames,
        dropdownType,
        tabsItems,
        dropdownTabsContent,
        loadDataFlag,
        locale = 'zh-cn',
        multiple = false,
        placeholder,
        allowClear = true,
        autoFocus = false,
        expandTrigger = 'click',
        expandIcon = null,
        suffixIcon = null,
        separator = '/',
        changeOnSelect = false,
        displayRender,
        dropdownRender: propsDropdownRender,
        notFoundContent,
        getPopupContainer,
        popupClassName: propsPopupClassName,
        popupPlacement,
        popupVisible: propsPopupVisible,
        loadData,
        maxTagCount,
        maxTagPlaceholder,
        maxTagTextLength,
        tagRender,
        showCheckedStrategy,
        onRef,
        onSearch,
        onChangeTabs,
        fieldid,
        browserClassName,
        browserStyle,
        browser: propsBrowser,
        bordered: propsBordered,
        align: propsAlign,
        size: propsSize,
        disabled: propsDisabled,
        readOnly: propsReadOnly,
        requiredStyle,
        dir: direction,
        resultListMatchInputWidth = false,
        ...restProps
    } = props;

    // ============ Context ============
    const context = React.useContext(ConfigContext);

    // 处理bordered等provider参数
    const browser = propsBrowser ?? context?.browser;
    const bordered = propsBordered ?? context?.bordered;
    const align = propsAlign ?? context?.align;
    const size = propsSize ?? context?.size;
    const disabled = propsDisabled ?? context?.disabled;
    const readOnly = propsReadOnly ?? context?.readOnly;

    // ============ Refs ============
    const inputRef = useRef<any>(null);
    const menuAddEventRef = useRef<any>(null);
    const restDataRef = useRef<any[]>([]);
    const matchingOptionsRef = useRef<CascaderOption[]>([]);

    // ============ 国际化 ============
    const local = getLangInfo(locale, i18n, 'cascader');

    // ============ State管理 (useReducer) ============
    const [state, dispatch] = useCascaderState({
        options: propsOptions,
        value: propsValue as any,
        defaultValue: defaultValue as any,
        locale: local,
        tabsItems,
        dropdownRender: propsDropdownRender,
        popupClassName: propsPopupClassName,
        inputValue: props.inputValue as any,
        popupVisible: propsPopupVisible,
    });

    // ============ Hooks集成 ============

    // 1. useValue Hook - value状态管理
    const {
        value,
        isClear,
        setValue,
    } = useValue({
        value: propsValue as any,
        defaultValue: defaultValue as any,
        multiple,
        onChange,
    });

    // 2. useOptions Hook - options处理
    const { fieldNames: filledFieldNames, flattenOptions, generateFilteredOptions } = useOptions({
        options: state.optionsState.options,
        showSearch,
        changeOnSelect,
        fieldNames: propsFieldNames,
        inputValue: state.inputState.inputValue,
        notFoundContent,
        locale,
        prefixCls,
    });

    // ============ 辅助方法 ============

    /**
     * defaultDisplayRender - 默认显示渲染
     */
    const defaultDisplayRender = useCallback(
        (label: (string | number)[] | CascaderOption[]) => {
            const labelKey = filledFieldNames.label || 'label';
            if (value.length > 0) {
                if (typeof value[0] === 'string') {
                    return label.length > 0 ? label.join(` ${separator} `) : '';
                } else if (typeof value[0] === 'object') {
                    let filterArr: (string | number)[] = [];
                    value.forEach((item: any) => {
                        filterArr.push(item.label ? item.label : item[labelKey]);
                    });
                    return filterArr.length > 0 ? filterArr.join(` ${separator} `) : '';
                }
            } else {
                return '';
            }
        },
        [value, separator, filledFieldNames]
    );

    /**
     * getLabel - 获取选中项的标签
     */
    const getLabel = useCallback(() => {
        if (!value || value.length === 0) {
            return '';
        }
        const unwrappedValue = Array.isArray(value[0]) ? value[0] : value;
        const valueKey = filledFieldNames.value || 'value';
        const childrenKey = filledFieldNames.children || 'children';
        const labelKey = filledFieldNames.label || 'label';
        const selectedOptions = arrayTreeFilter(
            state.optionsState.options as CascaderOption[],
            (o: CascaderOption, level: number) => o[valueKey] === unwrappedValue[level],
            { childrenKeyName: childrenKey }
        );
        let label = selectedOptions.length
            ? selectedOptions.map((o: CascaderOption) => o[labelKey])
            : value;
        if (direction === 'rtl') {
            label = Array.isArray(label) ? label.reverse() : label;
        }

        // 使用displayRender或defaultDisplayRender
        const renderFn = displayRender || defaultDisplayRender;
        return renderFn(label, selectedOptions);
    }, [value, state.optionsState.options, filledFieldNames, direction, displayRender, defaultDisplayRender]);

    /**
     * getPopupContainerDom - 获取弹出容器
     */
    const getPopupContainerDom = useCallback(
        (dom: HTMLElement) => {
            if (typeof getPopupContainer === 'function') {
                return getPopupContainer(dom);
            } else {
                return cssUtil.parentsUntil(dom);
            }
        },
        [getPopupContainer]
    );

    /**
     * getPopupPlacement - 获取弹出位置
     */
    const getPopupPlacementValue = useCallback(
        (dir = 'ltr') => {
            if (popupPlacement !== undefined) {
                return popupPlacement;
            }
            return dir === 'rtl' ? 'bottomRight' : 'bottomLeft';
        },
        [popupPlacement]
    );

    /**
     * getSelectedPath - 根据value获取选中路径
     */
    const getSelectedPath = useCallback(
        (options: any, val: any) => {
            const valueKey = filledFieldNames.value;
            const childKey = filledFieldNames.children;
            let selectValue: any[] = [];
            const dep = (options: any, val: any) => {
                if (options?.length === 0) {
                    return;
                }
                for (let i = 0; i < (options as any)?.length; i++) {
                    for (let j = 0; j < val?.length; j++) {
                        if (val[j] === options[i][valueKey]) {
                            selectValue.push(options[i]);
                        }
                    }
                    if (options![i][childKey]?.length) {
                        dep(options![i]?.children, val);
                    }
                }
            };
            dep(options, val);
            return selectValue;
        },
        [filledFieldNames]
    );

    /**
     * 更新options中的某个节点的children
     */
    const changeOptions = useCallback(
        (options: any, val: any) => {
            const valueKey = filledFieldNames.value;
            const childKey = filledFieldNames.children;
            const dep = (options: any, val: any) => {
                if (options?.length === 0) {
                    return;
                }
                for (let i = 0; i < (options as any)?.length; i++) {
                    if (val[valueKey as any] === options[i][valueKey as any]) {
                        options[i][childKey] = val[childKey];
                        break;
                    }
                    if (options![i][childKey]?.length) {
                        dep(options![i]?.children, val);
                    }
                }
            };
            dep(options, val);
            return options;
        },
        [filledFieldNames]
    );

    // ============ 事件处理器 ============

    /**
     * handleChange - 处理选择变化
     */
    const handleChange = useCallback(
        (newValue: any, selectedOptions: CascaderOption[]) => {
            let copySelectValue: any = getSelectedPath(state.optionsState.options, newValue);
            dispatch({ type: 'CLEAR_INPUT' });
            dispatch({ type: 'SET_IS_CLEAR', payload: false });

            // 处理过滤选项
            if (selectedOptions[0]?.__IS_FILTERED_OPTION) {
                const unwrappedValue =
                    selectedOptions[0][keepFilteredValueField] === undefined
                        ? newValue[0]
                        : selectedOptions[0][keepFilteredValueField];
                const unwrappedSelectedOptions = selectedOptions[0].path;
                setValue(unwrappedValue, unwrappedSelectedOptions);
                return;
            }

            if (showSearch) {
                dispatch({ type: 'SET_DROPDOWN_CLS', payload: false });
            }

            setValue(newValue, [...copySelectValue], [...selectedOptions]);
        },
        [showSearch, state.optionsState.options, getSelectedPath, setValue, dispatch]
    );

    /**
     * handlePopupVisibleChange - 处理弹窗显示/隐藏
     */
    const handlePopupVisibleChange = useCallback(
        (visible: boolean, isClearFlag?: any) => {
            if (!visible) {
                inputRef.current?.blur();
            }
            if (readOnly) {
                dispatch({ type: 'SET_POPUP_VISIBLE', payload: false });
                dispatch({ type: 'SET_INPUT_FOCUSED', payload: false });
                return;
            }

            if (!('popupVisible' in props)) {
                dispatch({ type: 'SET_POPUP_VISIBLE', payload: visible });
                dispatch({ type: 'SET_INPUT_FOCUSED', payload: visible });
                if (!visible) {
                    dispatch({ type: 'CLEAR_INPUT' });
                }
            }

            onPopupVisibleChange?.(visible, isClearFlag);
        },
        [readOnly, props, onPopupVisibleChange, dispatch, inputRef]
    );

    /**
     * handleClear - 清空选择
     */
    const handleClear = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (!state.inputState.inputValue) {
                handlePopupVisibleChange(false, 'isClear');
                setTimeout(() => {
                    setValue([]);
                }, 200);
            } else {
                dispatch({ type: 'SET_INPUT_VALUE', payload: '' });
            }

            dispatch({ type: 'CLEAR_INPUT' });
            dispatch({ type: 'SET_IS_CLEAR', payload: true });

            // 重置平铺模式tabs
            if (dropdownType === 'tiled' || dropdownType === '') {
                const newTabsArr = tabsItems
                    ? cloneDeep(tabsItems)
                    : [{ tab: local.langMap.placeholder, key: '1', content: state.optionsState.options }];
                dispatch({ type: 'SET_TABS_ARR', payload: newTabsArr });
                dispatch({ type: 'SET_TABS_ACTIVE_KEY', payload: tabsItems ? tabsItems[1]?.key : '1' });
                dispatch({ type: 'SET_DEEP_NUM', payload: tabsItems ? 1 : 0 });
            }
        },
        [
            state.inputState.inputValue,
            state.optionsState.options,
            dropdownType,
            tabsItems,
            local,
            handlePopupVisibleChange,
            setValue,
            dispatch,
        ]
    );

    /**
     * handleInputChange - 输入变化处理
     */
    const handleInputChange = useCallback(
        (e: string) => {
            const inputValue = e;

            // 只有在弹窗打开且有实际输入内容时才清空value
            // 避免在程序化清空输入框时误触发setValue([])
            if (state.popupState.popupVisible && inputValue !== '') {
                setValue([]);
            }

            if (!state.popupState.popupVisible) {
                handlePopupVisibleChange(true, '');
            }
            dispatch({ type: 'SET_INPUT_VALUE', payload: inputValue });
            dispatch({ type: 'SET_INIT_INPUT_VALUE', payload: inputValue });
            dispatch({ type: 'SET_IS_CLEAR', payload: false });

            // 设置焦点样式
            setFocusHandle(inputValue);

            // 触发onSearch回调
            onSearch?.(inputValue, matchingOptionsRef.current);
        },
        [state.popupState.popupVisible, handlePopupVisibleChange, setValue, onSearch, dispatch]
    );

    /**
     * handleInputClick - 输入点击处理
     */
    const handleInputClick = useCallback(
        (e: React.MouseEvent<HTMLInputElement>) => {
            if (state.inputState.inputFocused || state.popupState.popupVisible) {
                e.stopPropagation();
            }
        },
        [state.inputState.inputFocused, state.popupState.popupVisible]
    );

    /**
     * handleInputBlur - 输入失焦处理
     */
    const handleInputBlur = useCallback(() => {
        dispatch({ type: 'SET_INPUT_FOCUSED', payload: false });
    }, [dispatch]);

    /**
     * handleKeyDown - 键盘事件处理
     */
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === KeyCode.BACKSPACE || e.keyCode === KeyCode.SPACE) {
            e.stopPropagation();
        }
    }, []);

    /**
     * handleInputBox - 输入框点击处理
     */
    const handleInputBox = useCallback(() => {
        if (showSearch) {
            inputRef.current?.focus();
        }
        setFieldid();
    }, [showSearch]);

    /**
     * setFocusHandle - 设置焦点样式
     */
    const setFocusHandle = useCallback(
        (inputValue: string) => {
            if (inputValue !== '') {
                dispatch({ type: 'SET_DROPDOWN_CLS', payload: true });
            } else {
                dispatch({ type: 'SET_DROPDOWN_CLS', payload: false });
            }
        },
        [dispatch]
    );

    /**
     * onSearchInternal - 搜索回调
     */
    const onSearchInternal = useCallback(
        (inputValue: string) => {
            setFocusHandle(inputValue);
            onSearch?.(inputValue);
        },
        [onSearch, setFocusHandle]
    );

    /**
     * loadDataHandler - 动态加载处理
     */
    const loadDataHandler = useCallback(
        (selectedOptions: any) => {
            const currentOptions = selectedOptions[selectedOptions.length - 1];
            const valueKey = filledFieldNames.id || filledFieldNames.value;
            const lastValueKey = currentOptions[valueKey as string] || currentOptions.value;

            // 检查是否已加载过
            if (!state.optionsState.loadedKeys.includes(lastValueKey)) {
                dispatch({ type: 'ADD_LOADED_KEY', payload: lastValueKey });
            } else {
                return;
            }

            // 平铺模式下设置activeKey
            if (dropdownType === 'tiled') {
                dispatch({ type: 'SET_TABS_ACTIVE_KEY', payload: currentOptions[valueKey] });
            }

            loadData?.(selectedOptions);
        },
        [
            filledFieldNames,
            state.optionsState.loadedKeys,
            dropdownType,
            loadData,
            dispatch,
        ]
    );

    /**
     * onDelete - 删除多选标签
     */
    const onDelete = useCallback(
        (deletedValue: Record<string, any>) => {
            restDataRef.current = restDataRef.current?.filter(
                (item: Record<string, any>) => item.value !== deletedValue.value
            );
            let newValue = (value as unknown as Array<Array<string>>).filter(
                (item: Record<string, any>) =>
                    deletedValue.valueCells[deletedValue.valueCells.length - 1] !== item[item.length - 1]
            );
            if (restDataRef.current.length === 0) {
                dispatch({ type: 'SET_DROPDOWN_DOM', payload: propsDropdownRender });
                dispatch({ type: 'SET_DROPDOWN_MATCH_WIDTH', payload: false });
            }
            setValue(newValue as any);
        },
        [value, propsDropdownRender, setValue, dispatch]
    );

    /**
     * getDropDownNode - 获取下拉节点(多选溢出显示)
     */
    const getDropDownNode = useCallback(() => {
        const restData = restDataRef.current;
        return (
            <div className={`${prefixCls}-auto-tag-overflow`}>
                {restData?.map((node: any, index: number) => (
                    <div key={index}>
                        <span className={`${prefixCls}-auto-tag-overflow-item`}>
                            <span
                                title={node.label}
                                className={`${prefixCls}-auto-tag-overflow-item-label`}
                            >
                                {node.label || node.children}
                            </span>
                            {!node.disabled && (
                                <span className={`${prefixCls}-auto-tag-overflow-item-delete`}>
                                    <Icon onClick={() => onDelete(node)} type="uf-close" />
                                </span>
                            )}
                        </span>
                    </div>
                ))}
            </div>
        );
    }, [prefixCls, onDelete]);

    /**
     * onTagMouseDown - 多选标签鼠标按下
     */
    const onTagMouseDown = useCallback(
        (event: React.MouseEvent) => {
            const baseClassName = `${prefixCls}-selection-overflow-item`;
            const parent = cssUtil.parent(event.target as HTMLElement, `.${baseClassName}`);
            if (
                parent &&
                ((parent as HTMLElement).getAttribute('class') as string).includes(`${baseClassName}-rest`)
            ) {
                dispatch({ type: 'SET_DROPDOWN_DOM', payload: getDropDownNode });
                dispatch({ type: 'SET_DROPDOWN_MATCH_WIDTH', payload: true });
                dispatch({ type: 'SET_IS_MORE_TAG_CLICK', payload: true });
                return;
            }
            dispatch({ type: 'SET_DROPDOWN_DOM', payload: propsDropdownRender });
            dispatch({ type: 'SET_DROPDOWN_MATCH_WIDTH', payload: false });
            dispatch({ type: 'SET_IS_MORE_TAG_CLICK', payload: false });
        },
        [prefixCls, propsDropdownRender, getDropDownNode, dispatch]
    );

    /**
     * onRenderRest - 渲染剩余标签
     */
    const onRenderRest = useCallback(
        (values: Record<string, any>[]) => {
            if (values?.length && state.dropdownState.isMoreTagClick) {
                restDataRef.current = values;
            }
        },
        [state.dropdownState.isMoreTagClick]
    );

    /**
     * setFieldid - 设置fieldid
     */
    const setFieldid = useCallback(() => {
        if (fieldid) {
            const newFieldid = fieldid.split('|').join('');
            dispatch({ type: 'SET_POPUP_CLASSNAME', payload: propsPopupClassName || newFieldid });

            setTimeout(() => {
                const popupCls = state.popupState.popupClassName || newFieldid;
                const arrLabel = document.querySelectorAll(
                    `.${popupCls} .${prefixCls}-menus .${prefixCls}-menu-item`
                );
                if (expandTrigger === 'hover') {
                    arrLabel.forEach((node) => {
                        menuAddEventRef.current = addEventListener(node, 'mouseover', menuMouseoverHandle);
                    });
                } else {
                    arrLabel.forEach((node) => {
                        menuAddEventRef.current = addEventListener(node, 'click', menuMouseoverHandle);
                    });
                    setDomFieldid();
                }
            }, 200);
        }
    }, [fieldid, state.popupState.popupClassName, prefixCls, expandTrigger, propsPopupClassName, dispatch]);

    /**
     * setDomFieldid - 设置DOM的fieldid属性
     */
    const setDomFieldid = useCallback(() => {
        if (fieldid) {
            const popupCls = state.popupState.popupClassName || fieldid.split('|').join('');
            const arr = document.querySelectorAll(
                `.${popupCls} .${prefixCls}-menus .uf-arrow-right`
            );
            arr.forEach((item, index) => {
                item.setAttribute('fieldid', fieldid + '_expand_' + index);
            });
        }
    }, [fieldid, state.popupState.popupClassName, prefixCls]);

    /**
     * menuMouseoverHandle - 菜单鼠标悬停处理
     */
    const menuMouseoverHandle = useCallback(() => {
        setTimeout(() => {
            setDomFieldid();
        }, 200);
    }, [setDomFieldid]);

    /**
     * onTriggerOpen - 触发展开(处理禁用节点展开)
     */
    const onTriggerOpen = useCallback(
        (fullPathKey: string, option: any, isMergedLeaf: boolean) => {
            if (!isMergedLeaf && option.disabled && option.children && option.children.length > 0) {
                const valuePathArr = fullPathKey.split(VALUE_SPLIT);
                const loop = (options: any, pathArr: string[], level: number): any => {
                    let currentOptionsIndex = options.findIndex((item: any) => item.value === pathArr[level]);
                    if (currentOptionsIndex !== -1) {
                        if (level === pathArr.length - 1) {
                            let currentOptions = options[currentOptionsIndex];
                            currentOptions = {
                                ...currentOptions,
                                children: currentOptions.children.map((child: any) => ({
                                    ...child,
                                    disabled: true,
                                })),
                            };
                            return options.splice(currentOptionsIndex, 1, currentOptions);
                        }
                        return loop(options[currentOptionsIndex].children, pathArr, level + 1);
                    }
                };
                const oldOptions = state.optionsState.options;
                loop(oldOptions, valuePathArr, 0);
                dispatch({ type: 'SET_OPTIONS', payload: oldOptions ? [...oldOptions] : [] });
            }
        },
        [state.optionsState.options, dispatch]
    );

    // ============ 平铺模式处理 ============

    /**
     * setTabsItem - 设置tabs项
     */
    const setTabsItem = useCallback(
        (path: any[]) => {
            const labelKey = filledFieldNames.label;
            const valueKey = filledFieldNames.value;
            const childrenKey = filledFieldNames.children;
            let newArr: { key: string; tab: string; content: any }[] = [];
            let tiledValueCopy: string[] = [];

            if (path && path?.length > 0) {
                if (Array.isArray(path)) {
                    path.forEach((item: any, index: number) => {
                        if (index === 0) {
                            newArr.push({ key: '1', tab: item[labelKey], content: state.optionsState.options });
                        } else {
                            newArr.push({
                                key: path[index - 1][valueKey],
                                tab: item[labelKey],
                                content: path[index - 1][childrenKey],
                            });
                        }
                        if (index !== path?.length - 1) {
                            tiledValueCopy.push(item[valueKey]);
                        }
                    });
                }
            }
            dispatch({ type: 'SET_TABS_ARR', payload: newArr });
            dispatch({ type: 'SET_TABS_ACTIVE_KEY', payload: newArr[newArr.length - 1]?.key });
            dispatch({ type: 'SET_TILED_VALUE', payload: tiledValueCopy });
            dispatch({ type: 'SET_TILED_SELECTED_OPTIONS', payload: [...path] });
            dispatch({ type: 'SET_DEEP_NUM', payload: path?.length - 1 });
        },
        [filledFieldNames, state.optionsState.options, dispatch]
    );

    /**
     * searchTiledItem - 搜索平铺项点击
     */
    const searchTiledItem = useCallback(
        (val: { value: []; path: any; id?: any }) => {
            const valueKey = filledFieldNames.value;
            const labelKey = filledFieldNames.label;
            let newValue: any = val?.value || val?.id || [];
            let allPath: any = val?.path;
            if (!val?.value) {
                if (Array.isArray(val?.path)) {
                    val?.path?.forEach((item: Record<string, any>) => {
                        newValue.push(item[valueKey] as never);
                    });
                } else {
                    newValue = [{ value: val[valueKey], label: val[labelKey] }];
                    allPath = [{ value: val[valueKey], label: val[labelKey] }];
                }
            }

            // 第一个参数是value数组,第二个参数是selectedOptions(完整的option对象数组)
            setValue(newValue, allPath?.length > 0 ? allPath : newValue);

            // 先关闭弹窗,再清空输入框
            dispatch({ type: 'SET_POPUP_VISIBLE', payload: false });
            dispatch({ type: 'CLEAR_INPUT' });

            // 设置tabs项
            setTabsItem(val?.path);
            onChange?.(newValue, allPath)
        },
        [filledFieldNames, setValue, dispatch]
    );

    /**
     * onChangeTabs - tabs切换
     */
    const onChangeTabsHandler = useCallback(
        (val: string) => {
            let deepNum = tabsItems ? 1 : 0;
            state.tiledState.tabsArr?.forEach((item, index) => {
                if (item.key === val) {
                    deepNum = index;
                }
            });
            dispatch({ type: 'SET_TABS_ACTIVE_KEY', payload: val });
            dispatch({ type: 'SET_DEEP_NUM', payload: deepNum });
            onChangeTabs?.(val);
        },
        [state.tiledState.tabsArr, onChangeTabs, dispatch]
    );

    /**
     * onChangeValue - 平铺模式值变更
     */
    const onChangeValue = async(val: any) => {
        const labelKey = filledFieldNames.label;
        const valueKey = filledFieldNames.value;
        const childKey = filledFieldNames.children;
        const {
            tabsArr = [],
            deepNum = 0,
            tiledValue = [],
            tiledSelectedOptions = [],
        } = state.tiledState;
        // 深拷贝tabsArr,避免直接修改state中的对象
        let newArr = cloneDeep(tabsArr);

        // 更新当前层级的tab标签
        newArr.forEach((item: any, index: number) => {
            if (deepNum === index) {
                item.tab = val[labelKey];
            }
        });

        if (loadData) {
            if (
                (val && val.isLeaf !== undefined && !val.isLeaf) ||
                (val && val.isEnd !== undefined && !val.isEnd)
            ) {
                let splitArr = newArr.slice(0, deepNum + 1);
                let tiledValueCopy = [...tiledValue].slice(0, deepNum);
                let tiledSelectedOptionsCopy = [...tiledSelectedOptions].slice(0, deepNum);
                tiledValueCopy.push(val[valueKey]);
                tiledSelectedOptionsCopy.push(val);
                loadData(tiledSelectedOptionsCopy, (optionsV: any) => {
                    const vKey = valueKey || 'value';
                    const cKey = childKey || 'children';

                    splitArr.push({
                        tab: local.langMap.placeholder,
                        key: optionsV[vKey] || '',
                        content: optionsV[cKey],
                    });
                    // 创建options的深拷贝,避免直接修改state
                    const currentOptions = cloneDeep(state.optionsState.options);
                    const newData = changeOptions(currentOptions, optionsV);

                    dispatch({ type: 'SET_TABS_ARR', payload: splitArr });
                    dispatch({ type: 'SET_TABS_ACTIVE_KEY', payload: optionsV[vKey] || '' });
                    dispatch({ type: 'SET_DEEP_NUM', payload: deepNum + 1 });
                    dispatch({ type: 'SET_TILED_VALUE', payload: tiledValueCopy });
                    dispatch({ type: 'SET_TILED_SELECTED_OPTIONS', payload: tiledSelectedOptionsCopy });
                    dispatch({ type: 'SET_OPTIONS', payload: newData });
                });
                if (changeOnSelect && !val.NoSelected) {
                    setValue(tiledValueCopy as any);
                }
            } else {
                let newValueArr = tiledValue?.slice(0, deepNum);
                let newSelectValueArr = tiledSelectedOptions?.slice(0, deepNum);
                let splitArr = newArr.slice(0, deepNum + 1);
                newValueArr.push(val[valueKey]);
                newSelectValueArr.push(val);
                dispatch({ type: 'SET_POPUP_VISIBLE', payload: false });
                dispatch({ type: 'SET_TABS_ARR', payload: splitArr });
                setValue(newValueArr as any, newSelectValueArr);
            }
            dispatch({ type: 'SET_INPUT_FOCUSED', payload: false });
        } else {
            if (val[childKey] && (val[childKey] as []).length > 0) {
                let splitArr = newArr.slice(0, deepNum + 1);
                let tiledValueCopy = [...tiledValue].slice(0, deepNum);
                let tiledSelectedOptionsCopy = [...tiledSelectedOptions].slice(0, deepNum);
                splitArr.push({
                    tab: local.langMap.placeholder,
                    key: val[valueKey],
                    content: val[childKey],
                });
                tiledValueCopy.push(val[valueKey]);
                tiledSelectedOptionsCopy.push(val);
                dispatch({ type: 'SET_TABS_ARR', payload: splitArr });
                dispatch({ type: 'SET_TABS_ACTIVE_KEY', payload: val[valueKey] });
                dispatch({ type: 'SET_DEEP_NUM', payload: deepNum + 1 });
                dispatch({ type: 'SET_TILED_VALUE', payload: tiledValueCopy });
                dispatch({ type: 'SET_TILED_SELECTED_OPTIONS', payload: tiledSelectedOptionsCopy });
                if (changeOnSelect && !val.NoSelected) {
                    setValue(tiledValueCopy as any);
                }
            } else {
                let newValueArr = tiledValue?.slice(0, deepNum);
                let newSelectValueArr = tiledSelectedOptions?.slice(0, deepNum);
                let splitArr = newArr.slice(0, deepNum + 1);
                newValueArr.push(val[valueKey]);
                newSelectValueArr.push(val);
                dispatch({ type: 'SET_POPUP_VISIBLE', payload: false });
                dispatch({ type: 'SET_TABS_ARR', payload: splitArr });
                setValue(newValueArr as any, newSelectValueArr);
            }
        }
    }

    // 3. useTiledContent Hook - 平铺模式渲染
    const tiledContent = useTiledContent({
        showSearch: showSearch && dropdownType === 'tiled',
        inputValue: state.inputState.inputValue,
        tabsArr: state.tiledState.tabsArr,
        tabsActiveKey: state.tiledState.tabsActiveKey,
        prefixCls,
        fieldNames: filledFieldNames,
        loadDataFlag,
        dropdownTabsContent,
        input: inputRef.current ? { input: inputRef.current } : undefined,
        onSearchItemClick: searchTiledItem,
        onChangeTabs: onChangeTabsHandler,
        onChangeValue,
        generateFilteredOptions,
    });

    // ============ Ref方法暴露 ============
    // 创建ref实例对象
    const refInstance = useCallback(() => ({
        focus: () => {
            inputRef.current?.focus();
        },
        blur: () => {
            inputRef.current?.blur();
        },
        // 平铺模式方法,供外部调用
        searchTiledItem,
        onChangeValue,
    }), [searchTiledItem, onChangeValue]);

    // 暴露给forwardRef
    useImperativeHandle(ref, refInstance, [refInstance]);

    // ============ 生命周期 - useEffect替代componentDidMount/Update/Unmount ============

    // componentDidMount: 调用onRef回调
    useEffect(() => {
        // 调用onRef并传递组件实例
        // refInstance()返回包含所有暴露方法的对象
        onRef?.(refInstance());
    }, [onRef, refInstance]);

    // componentWillUnmount: 清理事件监听
    useEffect(() => {
        return () => {
            menuAddEventRef.current && menuAddEventRef.current.remove();
        };
    }, []);

    // 监听props.value变化(替代UNSAFE_componentWillReceiveProps)
    useEffect(() => {
        if (
            'value' in props &&
            Array.isArray(props.value) &&
            JSON.stringify(value) !== JSON.stringify(props.value)
        ) {
            dispatch({ type: 'SET_VALUE', payload: props.value as any });
        }
    }, [props.value]);

    useEffect(() => {
        if (props.options) {
            dispatch({ type: 'SET_OPTIONS', payload: props.options });
        }
    }, [props.options]);

    // 监听props.options变化
    useEffect(() => {
        if (props.options && props.options.length !== state.optionsState.options.length && !isClear) {
            if (isEqual(props.options, state.optionsState.options)) {
                return
            }

            dispatch({ type: 'SET_OPTIONS', payload: props.options });

            // 更新平铺模式tabs
            if (dropdownType === 'tiled' || dropdownType === '') {
                const newTabsArr = tabsItems
                    ? cloneDeep(tabsItems)
                    : [{ tab: local.langMap.placeholder, key: '1', content: props.options }];
                dispatch({ type: 'SET_TABS_ARR', payload: newTabsArr });
                dispatch({
                    type: 'SET_TABS_ACTIVE_KEY',
                    payload: tabsItems ? tabsItems[1]?.key : '1',
                });
            }
        }
    }, [props.options, isClear, dropdownType, tabsItems]);

    // 监听props.dropdownRender变化
    useEffect(() => {
        if ('dropdownRender' in props) {
            dispatch({ type: 'SET_DROPDOWN_DOM', payload: props.dropdownRender });
        }
    }, [props.dropdownRender]);

    // 监听props.tabsItems变化
    useEffect(() => {
        if (
            'tabsItems' in props &&
            props.tabsItems &&
            JSON.stringify(tabsItems) !== JSON.stringify(props.tabsItems)
        ) {
            const newTabsArr = props.tabsItems
                ? cloneDeep(props.tabsItems)
                : [{ tab: local.langMap.placeholder, key: '1', content: state.optionsState.options }];
            dispatch({ type: 'SET_TABS_ARR', payload: newTabsArr });
            dispatch({
                type: 'SET_TABS_ACTIVE_KEY',
                payload: props.tabsItems ? props.tabsItems[1]?.key : '1',
            });
            dispatch({ type: 'SET_DEEP_NUM', payload: 1 });
            dispatch({
                type: 'SET_TILED_VALUE',
                payload: props.tabsItems ? [props.tabsItems[1]?.key] : [],
            });
        }
    }, [props.tabsItems]);

    // 监听showSearch变化,更新flattenOptions
    useEffect(() => {
        if (showSearch && flattenOptions) {
            dispatch({ type: 'SET_FLATTEN_OPTIONS', payload: flattenOptions });
        }
    }, [showSearch, flattenOptions]);

    // ============ 渲染逻辑 ============

    /**
     * 浏览态渲染
     */
    const renderBrowser = (params: any) => {
        const {
            browserClassName: bClassName,
            browserStyle: bStyle,
            bordered: bBordered,
            value: bValue,
            prefixCls: bPrefixCls,
            fieldid: bFieldid,
        } = params;

        const wrapperCls = classNames(`${prefix}-component-browser`, {
            [`${prefix}-component-browser-border-bottom`]: bBordered === 'bottom',
            [`${prefix}-component-browser-${size}`]: !!size
        });

        const componentCls = classNames(`${bPrefixCls}-picker-browser`, {
            [bClassName]: bClassName,
        });

        return (
            <div className={wrapperCls}>
                <div
                    className={componentCls}
                    style={bStyle}
                    {...(bFieldid ? { fieldid: bFieldid } : {})}
                >
                    {bValue}
                </div>
            </div>
        );
    }

    // 获取label
    const label = getLabel();

    // 浏览态
    if (browser) {
        return renderBrowser({
            size,
            className,
            bordered,
            align,
            requiredStyle,
            value: label ?? state.inputState.initInputValue,
            prefixCls,
            fieldid,
            browserClassName,
            browserStyle,
        });
    }

    const isRtlLayout = direction === 'rtl';
    const pickerCls = classNames(`${prefixCls}-picker`, {
        [`${prefixCls}-picker-rtl`]: isRtlLayout,
        [`${prefixCls}-picker-with-value`]: state.inputState.inputValue,
        [`${prefixCls}-picker-disabled`]: disabled,
        [`${prefixCls}-picker-show-search`]: !!showSearch,
        [`${prefixCls}-picker-focused`]: state.inputState.inputFocused,
        ...setComponentClass({ clsPrefix: `${prefixCls}-picker`, bordered, align, requiredStyle, readOnly }),
    }, className);

    // 清除图标
    const clearIcon =
        (allowClear && !disabled && !readOnly && value?.length > 0) ||
        state.inputState.inputValue ||
        state.inputState.initInputValue ? (
                <span className={`${prefixCls}-picker-clear`}>
                    <Icon
                        type="uf-close-c"
                        onClick={handleClear}
                        fieldid={fieldid ? fieldid + '_close' : undefined}
                    />
                </span>
            ) : null;

    // 后缀图标
    let inputIcon = suffixIcon ? (
        <span className={`${prefixCls}-picker-arrow`}>{suffixIcon}</span>
    ) : (
        <span className={`${prefixCls}-picker-arrow`}>
            <Icon fieldid={fieldid ? fieldid + '_down' : undefined} type="uf-arrow-down" />
        </span>
    );

    // 展开图标
    let expandIconNode;
    if (expandIcon) {
        expandIconNode = expandIcon;
    } else {
        expandIconNode = <Icon type="uf-arrow-right" />;
    }

    // 适配nid、uitype
    let adapterNid = getNid(props);

    // Input组件
    let _input = (
        <span style={style} className={pickerCls} onClick={handleInputBox} {...adapterNid} fieldid={fieldid}>
            <span title={label?.toString()} className={`${prefixCls}-picker-label`}>
                {label}
            </span>
            <Input
                ref={inputRef}
                placeholder={value && value.length > 0 ? undefined : placeholder || local.langMap.placeholder}
                size={setComponentSize(size!, { defaultIsMd: true }) as SizeType | undefined}
                bordered={bordered}
                className={`${prefixCls}-input`}
                value={label ? '' : state.inputState.initInputValue}
                disabled={disabled}
                readOnly={readOnly || !showSearch}
                onClick={showSearch ? handleInputClick : undefined}
                onBlur={showSearch ? handleInputBlur : undefined}
                onKeyDown={handleKeyDown}
                onChange={showSearch ? handleInputChange : undefined}
                autoFocus={autoFocus}
                fieldid={fieldid ? fieldid + '-input' : undefined}
            />
            {clearIcon}
            {inputIcon}
        </span>
    );

    // 处理options
    let options: any = state.optionsState.options;
    if (options && options.length > 0) {
        if (state.inputState.inputValue) {
            options = generateFilteredOptions();
        }
    } else {
        const getEmptyNode = (names: FieldNames, content: string) => {
            return {
                [names.value!]: 'ANT_CASCADER_NOT_FOUND',
                [names.label!]: content || <Empty locale={locale} />,
                disabled: true,
                isEmptyNode: true,
            };
        };
        options = [getEmptyNode(filledFieldNames, notFoundContent as string)];
    }
    matchingOptionsRef.current = options;

    // 下拉菜单样式
    let dropdownMenuColumnStyle: React.CSSProperties = {};
    let isNotFound =
        (options as CascaderOption[]).length === 1 && (options as CascaderOption[])[0].isEmptyNode;
    if (isNotFound) {
        dropdownMenuColumnStyle.height = 'auto';
    }
    let resultListMatchInputWidthFlag = false;
    if (
        showSearch &&
        typeof showSearch === 'object' &&
        showSearch?.matchInputWidth !== undefined
    ) {
        resultListMatchInputWidthFlag = showSearch.matchInputWidth !== false;
    }
    if (resultListMatchInputWidthFlag || ((state.inputState.inputValue || isNotFound) && inputRef.current)) {
        dropdownMenuColumnStyle.width = (inputRef.current as any)?.input?.offsetWidth;
    }
    let dropdownMenuStyle: React.CSSProperties = {};
    if (inputRef.current && resultListMatchInputWidth) {
        dropdownMenuStyle.maxWidth = (inputRef.current as any)?.input?.offsetWidth;
        dropdownMenuStyle.overflowX = 'auto';
    }

    // popup类名
    const rcCascaderPopupClassName = classNames(state.popupState.popupClassName, {
        [`${prefixCls}-menu-focus`]: state.popupState.dropDownCls,
        [`${prefixCls}-menu-multiple`]: multiple,
        [`${prefixCls}-menu-${direction}`]: direction === 'rtl',
        [`${prefixCls}-menu-empty`]:
            (options as CascaderOption[]).length === 1 &&
            (options as CascaderOption[])[0].value === 'ANT_CASCADER_NOT_FOUND',
        [`${prefixCls}-menu-tiled`]: dropdownType === 'tiled',
    });

    // wrapper类名
    const wrapperClassName = classNames(className, {
        [`${prefixCls}-${setComponentSize(size!)}`]: setComponentSize(size!),
        [`${prefixCls}-allow-clear-show`]: multiple && allowClear,
        [`${prefixCls}-allow-clear-hidden`]: multiple && !allowClear,
        [`${prefixCls}-border-bottom`]: multiple && bordered && typeof bordered === 'string',
        [`${prefixCls}-border-show`]: multiple && bordered && typeof bordered === 'boolean',
        [`${prefixCls}-border-hidden`]: multiple && !bordered,
        [`${prefixCls}-align-${align}`]: multiple && align,
    });

    // 加载图标
    let loadingIcon = (
        <span className={`${prefixCls}-menu-item-loading-icon`}>
            <div className={`${prefixCls}-spinner-icon`}></div>
        </span>
    );

    // notFoundContent处理
    let mergedNotFound;
    if (notFoundContent !== undefined) {
        mergedNotFound = notFoundContent;
    } else {
        mergedNotFound = <span>{local.langMap.notFoundContent}</span>;
    }

    return (
        <RcCascader
            options={options as CascaderOption[]}
            prefixCls={prefixCls}
            getPopupContainer={getPopupContainerDom}
            value={value as (string | number)[]}
            open={state.popupState.popupVisible}
            onDropdownVisibleChange={handlePopupVisibleChange}
            onChange={handleChange}
            expandIcon={expandIconNode}
            loadingIcon={loadingIcon}
            placement={getPopupPlacementValue(direction) as any}
            disabled={disabled}
            readOnly={readOnly}
            expandTrigger={expandTrigger}
            dropdownMenuColumnStyle={dropdownMenuColumnStyle}
            changeOnSelect={changeOnSelect}
            dropdownRender={
                dropdownType === 'tiled' || (isClear && (dropdownType === 'tiled' || dropdownType === ''))
                    ? () => tiledContent
                    : typeof state.dropdownState.dropdownDom === 'function'
                        ? state.dropdownState.dropdownDom
                        : propsDropdownRender
                            ? propsDropdownRender
                            : undefined
            }
            fieldNames={filledFieldNames}
            loadData={loadDataHandler}
            dropdownClassName={rcCascaderPopupClassName}
            multiple={multiple}
            checkable={multiple ? <span className={`${prefixCls}-checkbox-inner`} /> : false}
            maxTagCount={maxTagCount}
            maxTagTextLength={maxTagTextLength}
            clearIcon={clearIcon}
            inputIcon={inputIcon}
            allowClear={true}
            showArrow={true}
            showCheckedStrategy={showCheckedStrategy}
            maxTagPlaceholder={maxTagPlaceholder}
            fieldid={fieldid}
            showSearch={showSearch}
            onSearch={onSearchInternal}
            autoFocus={autoFocus}
            className={wrapperClassName}
            tagRender={tagRender}
            displayRender={displayRender}
            onTriggerOpen={onTriggerOpen}
            style={style}
            placeholder={placeholder}
            {...adapterNid}
            onTagMouseDown={onTagMouseDown}
            onRenderRest={onRenderRest}
            dropdownMatchSelectWidth={state.dropdownState.dropdownMatchSelectWidth}
            notFoundContent={mergedNotFound}
            dropdownMenuStyle={dropdownMenuStyle}
            {...restProps}
        >
            {multiple ? undefined : _input}
        </RcCascader>
    );
});

Cascader.displayName = 'Cascader';

// 静态属性
(Cascader as any).SHOW_PARENT = SHOW_PARENT;
(Cascader as any).SHOW_CHILD = SHOW_CHILD;

export default Cascader;
